// Die Orte der Nebenauftraege — deterministisch, aus einer **eigenen** Folge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/sidequests.py`.**
//
// Ein Nebenauftrag ist eine Kette von Punkten im Feld. Wo sie liegen, wird
// gerechnet, nicht uebertragen — dieselbe Zusage wie beim Feld selbst.
//
// Der Unterschied zu Gegnern, Anomalien und Sonden ist die Bezugsgroesse: jene
// werden je Chunk gezogen, ein Schrittpunkt haengt an (Auftragsnummer,
// Schrittnummer) und beruehrt darum **keine** Kachel. Er verschiebt damit keine
// Erzziehung; einen eigenen Golden-Block hat er trotzdem, denn beide Seiten
// muessen denselben Punkt treffen (Gates FG-20, FG-30).
//
// Diese Datei importiert **kein Phaser** (Gate FG-18): die Karte zeichnet die
// Punkte, aber sie rechnet sie nicht.

import { balancing, sideQuestIndex, zonesFor } from '../data/balancing';
import type { SideQuest } from '../data/types';
import { TAU, jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';
import { eraSeed } from './field';

/**
 * Zonennummer, die **die Kapsel** meint statt eines Rings.
 *
 * Spiegel von `SIDE_ZONE_BASE`. Eine Ablieferung gehört an die Basis — das ist
 * der vierte Schritt der Kernschleife, nicht ein Sonderfall.
 */
export const SIDE_ZONE_BASE = -1;

/** Ein Ort im Feld. Der Puffer wird übergeben, nicht angelegt (Gate FG-14). */
export interface Point {
  x: number;
  y: number;
}

/** Innerer und äußerer Rand eines Zonenrings. */
export function zoneBand(zoneIdx: number, era: number): [number, number] {
  const zones = zonesFor(era);
  const world = balancing().world;
  const tiefe = balancing().side.finalZoneDepth;
  const index = Math.max(0, Math.min(zones.length - 1, zoneIdx));
  const inner = Math.max(zones[index].from, world.clearRadius * 2);
  const outer = index + 1 < zones.length ? zones[index + 1].from : zones[index].from + tiefe;
  return [inner, Math.max(outer, inner + tiefe * 0.25)];
}

/**
 * Der Ort eines Schritts. **Die Reihenfolge der Ziehungen ist Vertrag.**
 *
 * Schreibt in einen übergebenen Puffer: die Karte ruft das je Bild für jeden
 * offenen Schritt (Gate FG-14).
 */
export function sidePoint(
  questIndex: number,
  stepIndex: number,
  zoneIdx: number,
  seed: number,
  era: number,
  out: Point,
): Point {
  if (zoneIdx < 0) {
    const [x, y] = balancing().world.dockPoint;
    out.x = x; out.y = y;
    return out;
  }

  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(questIndex, stepIndex, welt), balancing().side.salt, welt));

  const angle = rng() * TAU;                                            // 1
  const span = rng();                                                   // 2

  const [inner, outer] = zoneBand(zoneIdx, era);
  const spread = balancing().side.spread;
  const mitte = (1 - spread) * 0.5;
  const radius = inner + (outer - inner) * (mitte + spread * span);
  out.x = jsRound(Math.cos(angle) * radius);
  out.y = jsRound(Math.sin(angle) * radius);
  return out;
}

/** Der Ort eines Schritts eines konkreten Auftrags. */
export function stepPoint(
  quest: SideQuest, stepIndex: number, seed: number, era: number, out: Point,
): Point {
  const index = Math.max(0, Math.min(quest.steps.length - 1, stepIndex));
  return sidePoint(sideQuestIndex(quest.id), index, quest.steps[index].zone, seed, era, out);
}

const puffer: Point = { x: 0, y: 0 };

/** Steht das Schiff nah genug am nächsten offenen Schritt? */
export function atStep(
  x: number, y: number, quest: SideQuest, stepIndex: number, seed: number, era: number,
): boolean {
  if (stepIndex < 0 || stepIndex >= quest.steps.length) return false;
  stepPoint(quest, stepIndex, seed, era, puffer);
  return Math.hypot(puffer.x - x, puffer.y - y) <= balancing().side.reach;
}

/** Der Spielstand, soweit ein Nebenauftrag ihn liest. */
export interface SideSave {
  built: number;
  era: number;
  sideSteps: Record<string, number>;
  sideClaimed: Record<string, boolean>;
}

/**
 * Steht der Auftrag überhaupt in der Liste?
 *
 * Zwei Riegel, beide nach vorn gerichtet: die Ära und die Ausbaustufe, die ihn
 * trägt. Ohne Karte findet niemand einen Punkt im endlosen Feld — der Riegel
 * ist also kein Kunstgriff, sondern genau das Werkzeug, das der Auftrag
 * verlangt. **Wortgleich zu `rules.side_unlocked`.**
 */
export function sideUnlocked(save: SideSave, quest: SideQuest, built: ReadonlySet<string>): boolean {
  return quest.minEra <= save.era && built.has(quest.needBuild);
}

export function sideStepsDone(save: SideSave, quest: SideQuest): number {
  return Math.min(quest.steps.length, save.sideSteps[quest.id] ?? 0);
}

export function sideDone(save: SideSave, quest: SideQuest): boolean {
  return sideStepsDone(save, quest) >= quest.steps.length;
}

export function sideClaimed(save: SideSave, quest: SideQuest): boolean {
  return Boolean(save.sideClaimed[quest.id]);
}

/**
 * Offene Schritte am Ort `(x, y)` abhaken. Gibt die betroffenen IDs zurück.
 *
 * **Ein Schritt zur Zeit und nur der nächste.** Die Kette ist eine Reihenfolge
 * — „hol den Kran, dann die Kisten, dann liefere ab" —, und zwei Schritte, die
 * zufällig nebeneinander liegen, dürfen sie nicht überspringen.
 *
 * Die Solo-Fassung von `rules.advance_side`. Dass es zwei gibt, ist der Preis
 * dafür, dass das Spiel ohne Backend spielbar bleibt; dass beide dasselbe tun,
 * sichern dieselben Testfälle auf beiden Seiten.
 */
export function advanceSide(
  save: SideSave, x: number, y: number, seed: number, built: ReadonlySet<string>, out: string[],
): string[] {
  out.length = 0;
  for (const quest of balancing().sideQuests) {
    if (!sideUnlocked(save, quest, built) || sideDone(save, quest)) continue;
    const index = sideStepsDone(save, quest);
    if (atStep(x, y, quest, index, seed, save.era)) {
      save.sideSteps[quest.id] = index + 1;
      out.push(quest.id);
    }
  }
  return out;
}

/**
 * Der sichtbare Name eines Schritts — „Auftrag: was dort zu tun ist".
 *
 * Er steht hier und nicht in der Szene, weil er aus **Daten** entsteht: Name
 * und Beschriftung kommen aus der Tabelle, nicht aus einem Satz im Zeichenpfad.
 */
export function sideQuestLabel(quest: SideQuest, stepIndex: number): string {
  const step = quest.steps[Math.max(0, Math.min(quest.steps.length - 1, stepIndex))];
  return `${quest.name} — ${step.label}`;
}

/** Wie viele Nebenaufträge fertig und noch nicht abgeholt sind. */
export function sideClaimableCount(save: SideSave, built: ReadonlySet<string>): number {
  let count = 0;
  for (const quest of balancing().sideQuests) {
    if (sideUnlocked(save, quest, built) && sideDone(save, quest) && !sideClaimed(save, quest)) {
      count++;
    }
  }
  return count;
}

/**
 * Der nächste offene Schritt eines Auftrags — für Karte und Kursziel.
 *
 * `null`, wenn der Auftrag fertig, gesperrt oder unbekannt ist: ein Marker auf
 * einem erledigten Schritt wäre ein Ziel, das nichts mehr auslöst.
 */
export function openStep(
  save: SideSave, quest: SideQuest, built: ReadonlySet<string>,
): number | null {
  if (!sideUnlocked(save, quest, built) || sideDone(save, quest)) return null;
  return sideStepsDone(save, quest);
}
